import {
  BLUEPRINT_VERSION,
  type BlueprintScheduleItem,
  type BlueprintShift,
  type BlueprintTimeSlot,
  type EventBlueprint,
  type RelTime,
} from './event-blueprint.js';

/**
 * Standardsets und Vorschau für Basar-Vorlagen (Plan 16, U16b). Ein Standardset ist eine Blaupause als **versionierte
 * Konstante**: Ändert sich ein Set, steigt seine `version`, der angelegte Basar merkt sich `system:<key>@<version>`.
 * Geldfelder (Provision, Gebühr) bleiben leer (0): Der Assistent verlangt sie ausdrücklich (Frage U16-2), ein still
 * übernommener Vorschlag landete sonst auf gedruckten Etiketten.
 *
 * Alle Zeiten sind relativ zum Basartag T (`RelTime`), Verkauf 9:00 bis 12:00. Nie in einem Set: Titel, Personen, Codes.
 */

// --- Grenzen und Prüfung einer Blaupause von außen ---------------------------------------------------------------------

/** Zeiten einer Vorlage liegen höchstens so viele Tage vor oder nach dem Basartag (Plan 16 §4.8.2). */
export const TEMPLATE_DAY_LIMIT = 14;
/** Das Anmeldefenster beginnt höchstens so viele Tage vor dem Basartag (und endet spätestens am Basartag). */
export const TEMPLATE_REGISTRATION_DAYS_BEFORE = 120;
/** Größe einer gespeicherten Blaupause in Bytes (UTF-8 des JSON). */
export const TEMPLATE_MAX_BYTES = 65_536;
/** Höchstens so viele eigene Vorlagen je Organisation (ohne Papierkorb). */
export const TEMPLATE_MAX_PER_ORG = 20;
export const TEMPLATE_NAME_MIN = 2;
export const TEMPLATE_NAME_MAX = 80;
export const TEMPLATE_DESCRIPTION_MAX = 300;

/** Sekunden seit Mitternacht des Basartags, nur zum Vergleichen (Tag × 86400 + Uhrzeit). */
function relKey(value: RelTime): number {
  const [hour = '0', minute = '0', second = '0'] = value.at.split(':');
  return value.day * 86_400 + Number(hour) * 3600 + Number(minute) * 60 + Number(second);
}

/**
 * Verstöße einer Blaupause gegen die engeren Grenzen für Standardsets und Vorlagen, als lesbare Sätze (leer = in Ordnung):
 * Zeiten −14 bis +14 Tage, Anmeldefenster −120 bis 0, Beginn vor Ende, Anmeldestart vor Anmeldeschluss, ein Vorrang braucht
 * einen Anmeldestart. Rein relativ gerechnet (ohne Zeitumstellung): Die Reihenfolge ändert sich durch sie nie.
 */
export function blueprintProblems(blueprint: EventBlueprint): string[] {
  const problems: string[] = [];
  const within = (value: RelTime | null, what: string): void => {
    if (value !== null && Math.abs(value.day) > TEMPLATE_DAY_LIMIT) {
      problems.push(
        `${what} liegt mehr als ${String(TEMPLATE_DAY_LIMIT)} Tage vom Basartag entfernt.`,
      );
    }
  };
  const before = (a: RelTime | null, b: RelTime | null, what: string): void => {
    if (a !== null && b !== null && relKey(a) >= relKey(b)) problems.push(what);
  };

  within(blueprint.startsAt, 'Der Beginn des Verkaufs');
  within(blueprint.endsAt, 'Das Ende des Verkaufs');
  within(blueprint.closesAt, 'Das Ende des Basars');
  before(blueprint.startsAt, blueprint.endsAt, 'Der Verkauf muss vor seinem Ende beginnen.');
  if (
    blueprint.endsAt !== null &&
    blueprint.closesAt !== null &&
    relKey(blueprint.closesAt) < relKey(blueprint.endsAt)
  ) {
    problems.push('Das Ende des Basars liegt vor dem Ende des Verkaufs.');
  }

  const { opensAt, closesAt, priorityLeadMinutes } = blueprint.registration;
  for (const [value, what] of [
    [opensAt, 'Der Anmeldestart'],
    [closesAt, 'Der Anmeldeschluss'],
  ] as const) {
    if (value !== null && (value.day < -TEMPLATE_REGISTRATION_DAYS_BEFORE || value.day > 0)) {
      problems.push(
        `${what} muss zwischen ${String(TEMPLATE_REGISTRATION_DAYS_BEFORE)} Tagen vor dem Basartag und dem Basartag liegen.`,
      );
    }
  }
  before(opensAt, closesAt, 'Die Anmeldung muss vor ihrem Schluss öffnen.');
  if (priorityLeadMinutes !== null && opensAt === null) {
    problems.push('Eine Vorab-Anmeldung braucht einen Anmeldestart.');
  }

  blueprint.helpers.shifts.forEach((shift, index) => {
    const what = `Schicht ${String(index + 1)} („${shift.name}“)`;
    within(shift.startsAt, `Der Beginn von ${what}`);
    within(shift.endsAt, `Das Ende von ${what}`);
    before(shift.startsAt, shift.endsAt, `${what} muss vor ihrem Ende beginnen.`);
  });
  blueprint.schedule.forEach((item, index) => {
    const what = `Ablaufplan-Eintrag ${String(index + 1)} („${item.label}“)`;
    within(item.startsAt, `Der Beginn von ${what}`);
    within(item.endsAt, `Das Ende von ${what}`);
    before(item.startsAt, item.endsAt, `${what} muss vor seinem Ende beginnen.`);
  });
  blueprint.timeSlots.forEach((slot, index) => {
    const what = `Zeitfenster ${String(index + 1)}`;
    within(slot.startsAt, `Der Beginn von ${what}`);
    within(slot.endsAt, `Das Ende von ${what}`);
    before(slot.startsAt, slot.endsAt, `${what} muss vor seinem Ende beginnen.`);
  });
  return problems;
}

// --- Vorschau ---------------------------------------------------------------------------------------------------------

export interface PreviewShift {
  name: string;
  /** „HH:MM“ (ohne Sekunden), `null` = ohne Zeit. */
  from: string | null;
  to: string | null;
  capacity: number | null;
}

export interface PreviewShiftDay {
  /** Tage relativ zum Basartag, `null` = Schichten ohne Zeit. */
  day: number | null;
  /** „Basartag“, „Vortag“, „Folgetag“, „3 Tage vorher“, „2 Tage danach“ oder „ohne Zeit“. */
  label: string;
  shifts: PreviewShift[];
}

/** Kompakte Vorschau für die Karte (reine Funktion, kein Personenbezug, kein Text aus Freifeldern außer Schichtnamen). */
export interface BlueprintPreview {
  shifts: { total: number; days: PreviewShiftDay[] };
  timeSlots: { dropoff: number; pickup: number };
  /** Zahl der Kassenplätze. */
  stations: number;
  /** Anmeldung öffnet so viele Tage vor dem Basartag (`null` = kein Anmeldestart). */
  registrationOpensDaysBefore: number | null;
  /** Vorab-Anmeldung früherer Verkäufer: Minuten vor dem Anmeldestart, `null` = keine. */
  priorityLeadMinutes: number | null;
  scheduleItems: number;
  /** Verkaufszeit am Basartag, falls gesetzt: „HH:MM“. */
  sale: { from: string | null; to: string | null };
  /** Abrechnung zur Anzeige (Geld in ganzen Cent, brutto wie überall). */
  settlement: { commissionBasisPoints: number; sellerFeeCents: number };
  slotBooking: EventBlueprint['registration']['slotBooking'];
  hasTerms: boolean;
}

/** „Basartag“, „Vortag“, „Folgetag“, sonst „N Tage vorher“ bzw. „N Tage danach“. */
export function relDayLabel(day: number): string {
  if (day === 0) return 'Basartag';
  if (day === -1) return 'Vortag';
  if (day === 1) return 'Folgetag';
  const n = Math.abs(day);
  return day < 0 ? `${String(n)} Tage vorher` : `${String(n)} Tage danach`;
}

const clock = (value: RelTime | null): string | null =>
  value === null ? null : value.at.slice(0, 5);

export function blueprintPreview(blueprint: EventBlueprint): BlueprintPreview {
  const byDay = new Map<number | null, PreviewShift[]>();
  for (const shift of blueprint.helpers.shifts) {
    const day = shift.startsAt === null ? null : shift.startsAt.day;
    const list = byDay.get(day) ?? [];
    list.push({
      name: shift.name,
      from: clock(shift.startsAt),
      to: clock(shift.endsAt),
      capacity: shift.capacity,
    });
    byDay.set(day, list);
  }
  const days: PreviewShiftDay[] = [...byDay.entries()]
    // Tage aufsteigend, „ohne Zeit“ zuletzt; innerhalb eines Tages nach Beginn (Reihenfolge der Blaupause bleibt bei Gleichstand)
    .sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : a - b))
    .map(([day, shifts]) => ({
      day,
      label: day === null ? 'ohne Zeit' : relDayLabel(day),
      shifts: [...shifts].sort((x, y) => (x.from ?? '99').localeCompare(y.from ?? '99')),
    }));
  const opens = blueprint.registration.opensAt;
  return {
    shifts: { total: blueprint.helpers.shifts.length, days },
    timeSlots: {
      dropoff: blueprint.timeSlots.filter((slot) => slot.kind === 'dropoff').length,
      pickup: blueprint.timeSlots.filter((slot) => slot.kind === 'pickup').length,
    },
    stations: blueprint.till.stations.length,
    registrationOpensDaysBefore: opens === null ? null : Math.max(0, -opens.day),
    priorityLeadMinutes: blueprint.registration.priorityLeadMinutes,
    scheduleItems: blueprint.schedule.length,
    sale: { from: clock(blueprint.startsAt), to: clock(blueprint.endsAt) },
    settlement: {
      commissionBasisPoints: blueprint.settlement.commissionBasisPoints,
      sellerFeeCents: blueprint.settlement.sellerFeeCents,
    },
    slotBooking: blueprint.registration.slotBooking,
    hasTerms: blueprint.terms !== null,
  };
}

// --- Standardsets ----------------------------------------------------------------------------------------------------

export interface SystemBlueprint {
  /** Stabiler Schlüssel (in `created_from`), nie umbenennen. */
  key: string;
  /** Steigt, sobald sich der Inhalt ändert. */
  version: number;
  name: string;
  /** Ein bis zwei Sätze für die Karte. */
  description: string;
  blueprint: EventBlueprint;
}

const t = (day: number, at: string): RelTime => ({ day, at });

function shift(name: string, startsAt: RelTime, endsAt: RelTime, capacity: number): BlueprintShift {
  return {
    name,
    startsAt,
    endsAt,
    capacity,
    location: null,
    description: null,
    signupMode: 'team',
    waitlistEnabled: true,
  };
}

function slot(
  kind: BlueprintTimeSlot['kind'],
  startsAt: RelTime,
  endsAt: RelTime,
  capacity: number,
): BlueprintTimeSlot {
  return { kind, startsAt, endsAt, capacity, location: null, note: null };
}

const LABEL_REMINDER: BlueprintScheduleItem = {
  label: 'Etiketten drucken',
  startsAt: t(-3, '09:00'),
  endsAt: null,
  note: 'Bitte drucken Sie Ihre Etiketten rechtzeitig vor der Annahme aus.',
  remindAudience: 'sellers_unprinted',
  remindDaysBefore: 0,
};

/** Gemeinsamer Rahmen: Verkauf 9:00 bis 12:00, Anmeldung T−28 18:00 bis T−3 23:59, Vorab-Anmeldung 60 Minuten, kein Geld. */
function base(overrides: Partial<EventBlueprint>): EventBlueprint {
  return {
    version: BLUEPRINT_VERSION,
    description: null,
    venue: null,
    startsAt: t(0, '09:00'),
    endsAt: t(0, '12:00'),
    closesAt: null,
    registration: {
      opensAt: t(-28, '18:00'),
      closesAt: t(-3, '23:59'),
      priorityLeadMinutes: 60,
      mode: 'approval',
      maxSellers: null,
      maxItemsPerSeller: 50,
      slotBooking: 'off',
    },
    items: {
      categories: null,
      sizes: null,
      photoRecognition: false,
      labelNameMode: 'number_first_name',
      colorRules: null,
      returnsBasket: false,
    },
    till: { discountPromosEnabled: false, stations: [] },
    settlement: {
      model: 'mediation',
      commissionBasisPoints: 0,
      sellerFeeCents: 0,
      payoutRoundingCents: 0,
    },
    helpers: { shifts: [], rules: null },
    schedule: [LABEL_REMINDER],
    timeSlots: [],
    terms: null,
    ...overrides,
  };
}

const station = (label: string): EventBlueprint['till']['stations'][number] => ({
  label,
  quickAccess: 'off',
  pin: false,
});

const SMALL_SHIFTS: BlueprintShift[] = [
  shift('Aufbau', t(-1, '15:00'), t(-1, '17:00'), 4),
  shift('Annahme', t(-1, '17:00'), t(-1, '19:30'), 6),
  shift('Sortieren', t(-1, '19:00'), t(-1, '21:00'), 4),
  shift('Kasse', t(0, '08:45'), t(0, '12:15'), 3),
  shift('Rückgabe sortieren', t(0, '12:15'), t(0, '14:00'), 6),
  shift('Abbau', t(0, '14:00'), t(0, '15:30'), 4),
];

/** Abgabe T−1 15:00 bis 20:00 in 30-Minuten-Schritten (je 15 Plätze). */
function dropoffSlots(): BlueprintTimeSlot[] {
  const slots: BlueprintTimeSlot[] = [];
  for (let minutes = 15 * 60; minutes < 20 * 60; minutes += 30) {
    const at = (value: number): string =>
      `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
    slots.push(slot('dropoff', t(-1, at(minutes)), t(-1, at(minutes + 30)), 15));
  }
  return slots;
}

const SMALL: SystemBlueprint = {
  key: 'klein',
  version: 1,
  name: 'Kleiner Basar',
  description:
    'Ein Verkaufsvormittag, Annahme am Vortag. Für bis zu 80 Verkäufer und ein kleines Team.',
  blueprint: base({
    helpers: { shifts: SMALL_SHIFTS, rules: null },
    till: { discountPromosEnabled: false, stations: [station('Kasse 1'), station('Kasse 2')] },
  }),
};

const LARGE: SystemBlueprint = {
  key: 'gross',
  version: 1,
  name: 'Großer Basar',
  description:
    'Zwei Tage: Annahme mit gebuchten Zeitfenstern, Verkauf mit vielen Helfern und Schichtwechsel.',
  blueprint: base({
    registration: { ...base({}).registration, slotBooking: 'closed' },
    helpers: {
      shifts: [
        shift('Aufbau', t(-1, '13:00'), t(-1, '16:00'), 10),
        shift('Annahme', t(-1, '15:00'), t(-1, '17:30'), 8),
        shift('Annahme', t(-1, '17:30'), t(-1, '20:00'), 8),
        shift('Sortieren', t(-1, '18:00'), t(-1, '21:00'), 8),
        shift('Vorsortieren', t(0, '07:30'), t(0, '08:45'), 6),
        shift('Kasse', t(0, '08:45'), t(0, '10:45'), 5),
        shift('Kasse', t(0, '10:45'), t(0, '12:15'), 5),
        shift('Rückgabe sortieren', t(0, '12:15'), t(0, '15:00'), 12),
        shift('Abbau', t(0, '15:00'), t(0, '16:30'), 8),
      ],
      // Absage nur mit Begründung, späte Absage (unter 48 Stunden) mit Freigabe
      rules: {
        cancelPolicy: 'reason',
        cancelLateHours: 48,
        cancelLatePolicy: 'approval',
        swapPolicy: 'direct',
        offerEnabled: true,
        offerNeedsApproval: false,
      },
    },
    till: {
      discountPromosEnabled: false,
      stations: [1, 2, 3, 4, 5].map((n) => station(`Kasse ${String(n)}`)),
    },
    timeSlots: [
      ...dropoffSlots(),
      slot('pickup', t(0, '15:30'), t(0, '16:15'), 30),
      slot('pickup', t(0, '16:15'), t(0, '17:00'), 30),
    ],
  }),
};

const KIDS: SystemBlueprint = {
  key: 'kinder',
  version: 1,
  name: 'Kinderbasar mit Kuchenstand',
  description: 'Wie der kleine Basar, dazu eine Schicht für Kuchen und Kaffee.',
  blueprint: base({
    helpers: {
      shifts: [...SMALL_SHIFTS, shift('Kuchenstand', t(0, '08:30'), t(0, '12:30'), 3)],
      rules: null,
    },
    till: { discountPromosEnabled: false, stations: [station('Kasse 1'), station('Kasse 2')] },
  }),
};

/** Die Standardsets in der Reihenfolge der Karten. */
export const SYSTEM_BLUEPRINTS: readonly SystemBlueprint[] = [SMALL, LARGE, KIDS];

export function findSystemBlueprint(key: string): SystemBlueprint | undefined {
  return SYSTEM_BLUEPRINTS.find((set) => set.key === key);
}

/** Kennzeichen am neuen Basar (`events.created_from`): `system:klein@1`, `template:<id>` oder `event:<id>`. */
export function createdFromOf(
  source:
    { kind: 'system'; key: string; version: number } | { kind: 'template' | 'event'; id: string },
): string {
  return source.kind === 'system'
    ? `system:${source.key}@${String(source.version)}`
    : `${source.kind}:${source.id}`;
}
